import { parseIsoCached } from "@/lib/utils";
import { noticeBadgeRank } from "./notice-badge";
import type { NoticeConversation } from "./notice-conversations";
import type { MentorWorkspaceId } from "./workspace";

export type NoticeSortMode = MentorWorkspaceId;

// Teens: unanswered inbound stays on top after the mentor reads it.
// Support: only Sendbird unread is a top-tier signal.
function sortTier(
  conversation: NoticeConversation,
  mode: NoticeSortMode,
): number {
  if (mode === "teens" && conversation.needsResponse) {
    return 0;
  }
  if (mode === "support" && conversation.unread) {
    return 0;
  }
  return 1 + noticeBadgeRank(conversation.badge);
}

function recencyMillis(conversation: NoticeConversation): number {
  const dt = parseIsoCached(conversation.lastMessageAt);
  return dt.isValid ? dt.toMillis() : Number.NaN;
}

function compareRecency(
  a: NoticeConversation,
  b: NoticeConversation,
  oldestFirst: boolean,
): number {
  const aMs = recencyMillis(a);
  const bMs = recencyMillis(b);
  const aValid = Number.isFinite(aMs);
  const bValid = Number.isFinite(bMs);
  if (aValid !== bValid) {
    return aValid ? -1 : 1;
  }
  if (!aValid) {
    return 0;
  }
  return oldestFirst ? aMs - bMs : bMs - aMs;
}

export function compareNoticeConversations(
  a: NoticeConversation,
  b: NoticeConversation,
  mode: NoticeSortMode = "teens",
): number {
  const tierDiff = sortTier(a, mode) - sortTier(b, mode);
  if (tierDiff !== 0) {
    return tierDiff;
  }
  // Badged teen rows stay oldest-waiting first. Unbadged teen rows are newest first.
  // Support unread stays newest first.
  const oldestFirst = mode === "support" ? !a.unread : Boolean(a.badge);
  return compareRecency(a, b, oldestFirst);
}

export function sortNoticeConversations(
  conversations: NoticeConversation[],
  mode: NoticeSortMode = "teens",
): NoticeConversation[] {
  return [...conversations].sort((a, b) =>
    compareNoticeConversations(a, b, mode),
  );
}
